import type { CabRepeatValue, CabScheduleEntry, ScheduleEntryFormData } from '../types/cabSchedule';
import {
  addDays,
  addMonthsPreserveDay,
  addYearsPreserveDay,
} from './cabRecurrenceGenerator';

export type ScheduleEntryFormErrors = Partial<
  Record<keyof ScheduleEntryFormData | 'dateTimeRange', string>
>;

/** Combine YYYY-MM-DD date (from picker) and HH:mm time into a Date. */
export function combineDateAndTime(
  datePart: string,
  timePart: string,
  allDay: boolean,
  endOfDay = false
): Date | null {
  if (!datePart) return null;
  const dateOnly = datePart.includes('T') ? datePart.split('T')[0] : datePart;
  if (allDay) {
    const [y, m, d] = dateOnly.split('-').map(Number);
    if ([y, m, d].some((n) => Number.isNaN(n))) return null;
    if (endOfDay) {
      return new Date(y, m - 1, d, 23, 59, 59, 999);
    }
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }
  if (!timePart) return null;
  const [hh, mm] = timePart.split(':').map(Number);
  const [y, m, d] = dateOnly.split('-').map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function padTimePart(value: number): string {
  return String(value).padStart(2, '0');
}

export function formatTimeFromIso(iso: string): string {
  const d = new Date(iso);
  return `${padTimePart(d.getHours())}:${padTimePart(d.getMinutes())}`;
}

export function formatDateForPicker(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}T00:00`;
}

function formatLocalDateForPicker(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}T00:00`;
}

function parsePickerDateOnly(value: string): Date | null {
  if (!value) return null;
  const dateOnly = value.includes('T') ? value.split('T')[0] : value;
  const [y, m, d] = dateOnly.split('-').map(Number);
  if ([y, m, d].some((n) => Number.isNaN(n))) return null;
  return new Date(y, m - 1, d);
}

/**
 * Default Repeats Until for Weekly / Monthly / Yearly based on Start Date.
 * Returns null for other repeat options (caller should not overwrite).
 */
export function computeDefaultRepeatsUntil(
  fromDate: string,
  repeats: CabRepeatValue
): string | null {
  if (repeats !== 'weekly' && repeats !== 'monthly' && repeats !== 'yearly') {
    return null;
  }

  const start = parsePickerDateOnly(fromDate);
  if (!start) return null;

  let until: Date;
  if (repeats === 'weekly') {
    until = addDays(start, 7);
  } else if (repeats === 'monthly') {
    until = addMonthsPreserveDay(start, 1);
  } else {
    until = addYearsPreserveDay(start, 1);
  }

  return formatLocalDateForPicker(until);
}

export function entryToFormData(entry: CabScheduleEntry): ScheduleEntryFormData {
  return {
    name: entry.name,
    fromDate: formatDateForPicker(entry.startDateTime),
    fromTime: formatTimeFromIso(entry.startDateTime),
    toDate: formatDateForPicker(entry.endDateTime),
    toTime: formatTimeFromIso(entry.endDateTime),
    allDay: entry.allDay,
    repeats: entry.repeats,
    repeatsUntil: entry.repeatsUntil ? formatDateForPicker(entry.repeatsUntil) : '',
    repeatInterval: entry.repeatInterval ?? 1,
    repeatWeekdays: Array.isArray(entry.repeatWeekdays) ? [...entry.repeatWeekdays] : [],
  };
}

export function validateScheduleEntryForm(data: ScheduleEntryFormData): ScheduleEntryFormErrors {
  const errors: ScheduleEntryFormErrors = {};

  const name = data.name.trim();
  if (!name) {
    errors.name = 'Name is required';
  } else if (name.length > 255) {
    errors.name = 'Name must be 255 characters or fewer';
  }

  if (!data.fromDate) {
    errors.fromDate = 'From Date is required';
  }
  if (!data.toDate) {
    errors.toDate = 'To Date is required';
  }
  if (!data.allDay) {
    if (!data.fromTime) errors.fromTime = 'From Time is required';
    if (!data.toTime) errors.toTime = 'To Time is required';
  }

  const start = combineDateAndTime(data.fromDate, data.fromTime, data.allDay, false);
  const end = combineDateAndTime(data.toDate, data.toTime, data.allDay, true);

  if (start && end) {
    if (end.getTime() <= start.getTime()) {
      errors.dateTimeRange = 'End Date/Time must be later than Start Date/Time';
    } else {
      const durationMs = end.getTime() - start.getTime();
      if (durationMs <= 0) {
        errors.dateTimeRange = 'Meeting duration must be greater than zero';
      }
      if (!data.allDay && durationMs < 60_000) {
        errors.dateTimeRange = 'Meeting duration must be greater than one minute';
      }
    }
  }

  if (data.repeats === undefined || data.repeats === null) {
    errors.repeats = 'Repeats is required';
  }

  const repeatsRequired = data.repeats !== '';
  if (repeatsRequired) {
    if (!data.repeatsUntil) {
      errors.repeatsUntil = 'Repeats Until is required when a repeat pattern is selected';
    } else if (start) {
      const until = combineDateAndTime(data.repeatsUntil, '00:00', false, true);
      if (until && startOfDay(until) < startOfDay(start)) {
        errors.repeatsUntil = 'Repeats Until must be greater than or equal to the Start Date';
      }
    }
  }

  if (data.repeats === 'specific') {
    const interval = Number(data.repeatInterval);
    if (!Number.isFinite(interval) || interval < 1 || !Number.isInteger(interval)) {
      errors.repeatInterval = 'Repeat Every must be a whole number greater than 0';
    }
    if (!data.repeatWeekdays || data.repeatWeekdays.length === 0) {
      errors.repeatWeekdays = 'Select at least one weekday';
    }
  }

  return errors;
}

export function hasScheduleValidationErrors(errors: ScheduleEntryFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

export function createDefaultScheduleEntryFormData(): ScheduleEntryFormData {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  const dateStr = `${y}-${m}-${d}T00:00`;

  return {
    name: '',
    fromDate: dateStr,
    fromTime: '15:00',
    toDate: dateStr,
    toTime: '16:00',
    allDay: false,
    repeats: '',
    repeatsUntil: '',
    repeatInterval: 1,
    repeatWeekdays: [],
  };
}

export function buildScheduleEntryPayload(
  cabDefinitionId: string,
  formData: ScheduleEntryFormData,
  active = true
) {
  const start = combineDateAndTime(formData.fromDate, formData.fromTime, formData.allDay, false);
  const end = combineDateAndTime(formData.toDate, formData.toTime, formData.allDay, true);
  if (!start || !end) {
    throw new Error('Invalid date/time values');
  }

  let repeatsUntil: string | null = null;
  if (formData.repeats !== '' && formData.repeatsUntil) {
    const until = combineDateAndTime(formData.repeatsUntil, '23:59', false, true);
    repeatsUntil = until?.toISOString() ?? null;
  }

  const payload: {
    cabDefinitionId: string;
    name: string;
    startDateTime: string;
    endDateTime: string;
    allDay: boolean;
    repeats: CabRepeatValue;
    repeatsUntil: string | null;
    active: boolean;
    repeatInterval?: number;
    repeatWeekdays?: number[];
  } = {
    cabDefinitionId,
    name: formData.name.trim(),
    startDateTime: start.toISOString(),
    endDateTime: end.toISOString(),
    allDay: formData.allDay,
    repeats: formData.repeats,
    repeatsUntil,
    active,
  };

  if (formData.repeats === 'specific') {
    payload.repeatInterval = Number(formData.repeatInterval) || 1;
    payload.repeatWeekdays = [...formData.repeatWeekdays];
  }

  return payload;
}
